import { useEffect, useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { fmtMY } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Copy, Trash, FileText, LinkSimple, CheckCircle, PencilSimple, FilePdf, UploadSimple } from "@phosphor-icons/react";
import RichTextEditor from "@/components/RichTextEditor";
import PlacementEditor from "@/components/PlacementEditor";
import { toHtml, sanitize, htmlToPlain } from "@/lib/richtext";

const empty = {
  resolution_type: "director",
  template_key: "",
  title: "",
  body: "",
  meeting_date: "",
  signatories: [],
};

function downloadPdf(rid) {
  const token = localStorage.getItem("cosec_token");
  // cache-buster: force browser to fetch a fresh PDF each time the button is
  // clicked. Without this, some browsers (Edge PDF viewer especially) serve
  // a stale copy after signatures / placements are added.
  const bust = Date.now();
  window.open(`${API}/resolutions/${rid}/pdf?auth=${token}&_=${bust}`, "_blank");
}

export default function ResolutionsTab({ client, categories = ["resolution"], label = "Board & Members' Resolutions", newLabel = "New Resolution", emptyHint = "Pick a template (Directors' Circular, Members' Written, etc.) to auto-fill and send for e-signature." }) {
  const isFormsLetters = categories.includes("form") || categories.includes("letter");
  const typeOptions = isFormsLetters
    ? [{ value: "director", label: "Form" }, { value: "member", label: "Letter" }]
    : [{ value: "director", label: "Directors' Resolution" }, { value: "member", label: "Members' Resolution" }];
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [viewRes, setViewRes] = useState(null);
  const [uploading, setUploading] = useState(false);
  const uploadRef = useRef(null);
  const topUploadRef = useRef(null);

  const uploadPdf = async (file, { autoSave = false } = {}) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("preserve_layout", "true");
      const token = localStorage.getItem("cosec_token");
      const res = await fetch(`${API}/custom-templates/extract-from-pdf`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text() || "Upload failed");
      const data = await res.json();
      if (autoSave) {
        // Open the Edit dialog so the drafter can choose WHO signs and HOW
        // MANY people sign before saving. No auto-save with all directors +
        // secretaries — signatories start empty; the drafter picks from the
        // "Add from client profile" list.
        setEditing(null);
        const cat = categories[0] || "form";
        setForm({
          resolution_type: cat === "letter" ? "member" : "director",
          template_key: "",
          title: data.title || file.name.replace(/\.pdf$/i, ""),
          body: data.body,
          meeting_date: new Date().toISOString().slice(0, 10),
          signatories: [],
          category: cat,
        });
        setOpen(true);
      } else {
        setForm(f => ({
          ...f,
          template_key: "",
          title: data.title,
          body: data.body,
        }));
        toast.success(`Loaded "${data.title}" — add signatories and send for e-signature`);
      }
    } catch (e) { toast.error(String(e.message || e).slice(0, 240)); }
    finally {
      setUploading(false);
      if (uploadRef.current) uploadRef.current.value = "";
      if (topUploadRef.current) topUploadRef.current.value = "";
    }
  };

  const categoriesKey = categories.join(",");
  const load = () => api.get(`/clients/${client.id}/resolutions`).then(r => {
    const filtered = r.data.filter(x => categories.includes(x.category || "resolution"));
    setItems(filtered);
  });

  useEffect(() => {
    load();
    // Fetch templates for each requested category and merge (owner-scoped on server)
    Promise.all(categories.map(c => api.get(`/resolution-templates?category=${c}`).then(r => r.data)))
      .then(lists => setTemplates(lists.flat()));

    // Auto-refresh the list when the drafter returns to this tab — signatures
    // happen on the signer's device, so the drafter's browser is otherwise
    // stale. This keeps status (DRAFT → SENT → COMPLETED) in sync and makes
    // the newly-archived Documents entry visible without a hard reload.
    const onFocus = () => load();
    const onVisibility = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    // Poll every 30s as a fallback while the tab is open — cheap read.
    const iv = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 30000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(iv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id, categoriesKey]);

  const openNew = () => {
    setEditing(null);
    // seed signatories from directors by default
    const seeded = (client.directors || []).map(d => ({
      name: d.name, role: d.role || "Director", email: "", ic_no: d.ic_no || "",
    }));
    setForm({ ...empty, meeting_date: new Date().toISOString().slice(0, 10), signatories: seeded });
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      resolution_type: r.resolution_type,
      template_key: r.template_key || "",
      title: r.title,
      body: r.body,
      meeting_date: r.meeting_date || "",
      signatories: (r.signatories || []).map(s => ({
        name: s.name, role: s.role, email: s.email, ic_no: s.ic_no,
        placement: s.placement || null,
      })),
    });
    setOpen(true);
  };

  const pickTemplate = async (key) => {
    if (!key) { setForm(f => ({ ...f, template_key: "" })); return; }
    const tpl = templates.find(t => t.key === key);
    if (!tpl) return;
    // render for client
    try {
      const { data } = await api.get(
        `/resolution-templates/${key}/render?client_id=${client.id}&meeting_date=${encodeURIComponent(form.meeting_date || "")}`
      );
      // If directors template, seed with directors; member template, seed with shareholders
      const isDir = data.type === "director";
      const seedList = isDir ? (client.directors || []) : (client.shareholders || []);
      const seeded = seedList.map(x => ({
        name: x.name, role: isDir ? (x.role || "Director") : "Shareholder", email: "", ic_no: x.ic_no || "",
      }));
      setForm(f => ({
        ...f,
        template_key: key,
        resolution_type: data.type,
        title: data.title,
        body: data.body,
        signatories: f.signatories.length ? f.signatories : seeded,
      }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const save = async (thenSend = false, thenDownload = false) => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and body are required");
    if (form.signatories.length === 0) return toast.error("Add at least one signatory");
    // Forms & Letters: warn about unplaced signatories but don't block save.
    // Any unplaced signature will fall onto the appended signatures page —
    // strictly worse than an on-letter placement but never lost. Blocking
    // save just leaves signatures on the fallback page indefinitely because
    // the drafter can't push any partial fix. Confirm once so they know.
    if (isFormsLetters) {
      const unplaced = form.signatories.filter(s => !s.placement).map(s => s.name || "(unnamed)");
      if (unplaced.length) {
        const ok = window.confirm(
          `${unplaced.length} signatory box(es) not placed on the letter yet:\n\n  • ${unplaced.join("\n  • ")}\n\n` +
          `Their signatures will appear on an appended "Signatures" page at the end of the PDF instead of on the letter.\n\n` +
          `Save anyway?`
        );
        if (!ok) return;
      }
    }
    setSaving(true);
    try {
      let rid = editing?.id;
      const payload = { ...form };
      // Ensure Forms & Letters land in the correct category even when creating fresh
      if (!editing && isFormsLetters && !payload.category) {
        payload.category = categories[0] || "form";
      }
      if (editing) {
        await api.put(`/resolutions/${editing.id}`, payload);
      } else {
        const { data } = await api.post(`/clients/${client.id}/resolutions`, payload);
        rid = data?.id;
      }
      if (thenSend && rid) {
        await api.post(`/resolutions/${rid}/send`);
        toast.success("Saved and sent for signing");
      } else if (editing && editing.status === "completed") {
        // Re-placing signatures on an already-signed document → rebuild the
        // archived PDF so the Documents tab entry shows the corrected layout.
        try {
          await api.post(`/resolutions/${editing.id}/regenerate-archive`);
          toast.success("Signatures re-placed and signed PDF rebuilt — opening the new PDF now");
        } catch (e) {
          toast.warning("Saved, but rebuild failed — click Regenerate to retry");
        }
        // Auto-open the fresh PDF so the drafter immediately sees the result
        // without having to hunt for the PDF button after closing the dialog.
        if (thenDownload || editing.status === "completed") {
          downloadPdf(editing.id);
        }
      } else {
        toast.success("Saved");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const useLastSignatories = () => {
    if (!items.length) return toast.info("No previous resolution to copy from");
    const last = items[0];
    const seeded = (last.signatories || []).map(s => ({
      name: s.name, role: s.role || "", email: s.email || "", ic_no: s.ic_no || "",
    }));
    setForm(f => ({ ...f, signatories: seeded }));
    toast.success(`Loaded ${seeded.length} signatories from last resolution`);
  };
  void useLastSignatories;


  const remove = async (r) => {
    if (!window.confirm("Delete this resolution?")) return;
    await api.delete(`/resolutions/${r.id}`); load();
  };

  const send = async (r) => {
    await api.post(`/resolutions/${r.id}/send`);
    toast.success("Marked as sent. Share e-signing links from the row.");
    load();
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/sign/${token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  };

  const copyAllPendingLinks = async () => {
    // Group all unsigned links by signatory name so the drafter can paste the
    // whole message once into WhatsApp / email — every director gets ALL
    // their pending documents with the correct link, without picking one by
    // one.
    const origin = window.location.origin;
    const bySigner = new Map(); // name -> [{doc, url}]
    for (const r of items) {
      for (const s of r.signatories || []) {
        if (s.signed_at) continue;
        if (!s.sign_token) continue; // not sent yet
        const key = s.name || "Signatory";
        if (!bySigner.has(key)) bySigner.set(key, { role: s.role || "", rows: [] });
        bySigner.get(key).rows.push({ doc: r.title, url: `${origin}/sign/${s.sign_token}` });
      }
    }
    if (bySigner.size === 0) {
      return toast.error("No pending e-sign links. Click Send on the resolutions first.");
    }
    const lines = [];
    lines.push(`Please e-sign the following document${bySigner.size > 1 || [...bySigner.values()][0].rows.length > 1 ? "s" : ""} for ${client.name}:`);
    lines.push("");
    for (const [name, { role, rows }] of bySigner) {
      lines.push(`— ${name}${role ? ` (${role})` : ""} —`);
      for (const { doc, url } of rows) {
        lines.push(`• ${doc}`);
        lines.push(`  ${url}`);
      }
      lines.push("");
    }
    lines.push("Thank you.");
    const text = lines.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${[...bySigner.values()].reduce((a, v) => a + v.rows.length, 0)} link(s) for ${bySigner.size} signer(s) — paste into WhatsApp / email`);
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const regenerateArchive = async (r) => {
    try {
      await api.post(`/resolutions/${r.id}/regenerate-archive`);
      toast.success("Signed PDF rebuilt — the Documents tab entry is now up to date");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to regenerate");
    }
  };

  const addSignatory = () => setForm(f => ({ ...f, signatories: [...f.signatories, { name: "", role: "", email: "", ic_no: "" }] }));
  const updSignatory = (i, k, v) => setForm(f => { const n = [...f.signatories]; n[i] = { ...n[i], [k]: v }; return { ...f, signatories: n }; });
  const rmSignatory = (i) => setForm(f => ({ ...f, signatories: f.signatories.filter((_, x) => x !== i) }));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          {label} · {items.length}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-sm h-9"
                  onClick={copyAllPendingLinks}
                  data-testid="copy-all-links-btn"
                  title="Copy every pending signer's document + link, grouped by name, ready to paste into WhatsApp or email">
            <Copy size={14} weight="bold" className="mr-1.5" /> Copy sign links
          </Button>
          <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]"
                  onClick={() => {
                    if (isFormsLetters) {
                      topUploadRef.current?.click();
                    } else {
                      openNew();
                    }
                  }}
                  disabled={isFormsLetters && uploading}
                  data-testid="add-resolution-btn">
            <Plus size={16} weight="bold" className="mr-2" /> {isFormsLetters && uploading ? "Uploading…" : newLabel}
          </Button>
          {isFormsLetters && (
            <input ref={topUploadRef} type="file" accept="application/pdf" hidden
                   onChange={e => uploadPdf(e.target.files?.[0], { autoSave: true })}
                   data-testid="upload-form-pdf-top-input" />
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-sm text-muted-foreground">
          <FileText size={22} weight="duotone" className="mb-3" />
          {`No items yet. ${emptyHint}`}
        </div>
      ) : items.map(r => {
        const signed = r.signatories.filter(s => s.signed_at).length;
        const total = r.signatories.length;
        const complete = signed === total && total > 0;
        return (
          <div key={r.id} className="border border-border mb-4" data-testid={`resolution-${r.id}`}>
            <header className="px-6 py-3 border-b border-border flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {isFormsLetters ? r.status : `${r.resolution_type === "director" ? "Directors'" : "Members'"} · ${r.status}`}
                </div>
                <div className="font-medium">{r.title}</div>
                {isFormsLetters && r.status === "completed" && (() => {
                  const unplacedSigned = (r.signatories || []).filter(s => s.signed_at && !s.placement);
                  if (unplacedSigned.length === 0) return null;
                  return (
                    <button type="button"
                            onClick={() => openEdit(r)}
                            className="mt-2 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#8A6D0B] bg-[#FFF8DC] border border-[#F1C21B] px-3 py-1.5 hover:bg-[#FFEFB0] cursor-pointer"
                            data-testid={`unplaced-warning-${r.id}`}>
                      <span>⚠</span>
                      <span>
                        {unplacedSigned.length} signature{unplacedSigned.length > 1 ? "s are" : " is"} on the appended page (not on the letter).
                        Click here to open the placement editor and drop {unplacedSigned.length > 1 ? "them" : "it"} onto the letter.
                      </span>
                    </button>
                  );
                })()}
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-mono ${complete ? "text-[#24A148]" : "text-muted-foreground"}`}>
                  {signed}/{total} signed
                </span>
                <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => setViewRes(r)}>Preview</Button>
                <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => downloadPdf(r.id)}
                        data-testid={`download-pdf-${r.id}`}>
                  <FilePdf size={14} weight="bold" className="mr-1" /> PDF
                </Button>
                {(r.status === "draft" || (isFormsLetters && r.status === "completed")) && (() => {
                  const hasUnplaced = isFormsLetters && r.status === "completed" &&
                    (r.signatories || []).some(s => s.signed_at && !s.placement);
                  const cls = hasUnplaced
                    ? "rounded-sm h-8 bg-[#0F62FE] hover:bg-[#0043CE] text-white"
                    : "rounded-sm h-8";
                  return (
                    <Button size="sm" variant={hasUnplaced ? "default" : "outline"}
                            className={cls}
                            onClick={() => openEdit(r)}
                            data-testid={`edit-resolution-${r.id}`}
                            title={r.status === "completed" ? "Drop each signature onto the letter — signatures are preserved" : "Edit"}>
                      <PencilSimple size={14} className="mr-1" />
                      {r.status === "completed" ? "Place signatures" : "Edit"}
                    </Button>
                  );
                })()}
                {r.status === "completed" && !((r.signatories || []).some(s => s.signed_at && !s.placement)) && (
                  <Button size="sm" variant="outline" className="rounded-sm h-8"
                          onClick={() => regenerateArchive(r)}
                          data-testid={`regenerate-archive-${r.id}`}
                          title="Rebuild the archived signed PDF from current placements">
                    Regenerate
                  </Button>
                )}
                {r.status === "draft" && (
                  <Button size="sm" className="rounded-sm h-8 bg-[#0F62FE] hover:bg-[#0043CE]" onClick={() => send(r)}>Send</Button>
                )}
                <Button size="sm" variant="ghost" className="text-[#DA1E28]" onClick={() => remove(r)}>
                  <Trash size={14} />
                </Button>
              </div>
            </header>

            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-border">
                  <Th>Signatory</Th><Th>Role</Th><Th>Status</Th><Th>Signed at</Th><Th className="text-right">E-sign link</Th>
                </tr>
              </thead>
              <tbody>
                {r.signatories.map(s => (
                  <tr key={s.id} className="data-row border-b border-border">
                    <td className="px-6 py-2">
                      <div>{s.name}</div>
                      {s.email && <div className="text-[11px] text-muted-foreground">{s.email}</div>}
                    </td>
                    <td className="px-6 py-2 text-xs">{s.role}</td>
                    <td className="px-6 py-2">
                      {s.signed_at ? (
                        <span className="inline-flex items-center gap-1 text-[#24A148] text-xs font-mono uppercase">
                          <CheckCircle size={14} weight="fill" /> Signed
                        </span>
                      ) : <span className="text-xs font-mono uppercase text-muted-foreground">Pending</span>}
                    </td>
                    <td className="px-6 py-2 font-mono text-xs">{s.signed_at ? fmtMY(s.signed_at) : "—"}</td>
                    <td className="px-6 py-2 text-right">
                      <Button size="sm" variant="outline" className="rounded-sm h-8"
                              onClick={() => copyLink(s.sign_token)}
                              data-testid={`copy-sign-link-${s.id}`}>
                        <LinkSimple size={14} weight="bold" className="mr-1.5" /> Copy link
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* Editor dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl w-[95vw] rounded-sm max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing?.status === "completed" ? "Place signatures on" : (editing ? "Edit" : (isFormsLetters ? "Edit" : "New"))} {isFormsLetters ? "Form / Letter" : "Resolution"}
            </DialogTitle>
            {editing?.status === "completed" && isFormsLetters && (() => {
              const unplaced = form.signatories.filter(s => !s.placement);
              const placed = form.signatories.length - unplaced.length;
              return (
                <div className="mt-2 border border-[#F1C21B] bg-[#FFF8DC] px-4 py-3">
                  <div className="font-mono text-xs uppercase tracking-widest text-[#8A6D0B] mb-2">
                    How to move signatures onto the letter
                  </div>
                  <ol className="text-xs text-foreground space-y-1 list-decimal pl-4">
                    <li>Look at the left panel below — every name says <b>"not placed"</b> or <b>"Page N · placed"</b>.</li>
                    <li>Click a "not placed" name to select it (it gets a blue border).</li>
                    <li>Then click the exact spot on the letter (right side) where that person's signature should appear. A coloured box appears.</li>
                    <li>Drag the box to nudge; use the trash icon to clear a placement and redo.</li>
                    <li>Repeat until every name shows "Page N · placed", then click <b>Save Form / Letter</b> at the bottom.</li>
                  </ol>
                  <div className="mt-2 font-mono text-[11px] text-[#8A6D0B]">
                    Progress: {placed} / {form.signatories.length} placed
                    {unplaced.length > 0 && ` — still to place: ${unplaced.map(s => s.name).join(", ")}`}
                  </div>
                </div>
              );
            })()}
          </DialogHeader>
          {isFormsLetters ? (
            <PlacementEditor
              body={form.body}
              signatories={form.signatories}
              onChange={(next) => setForm(f => ({ ...f, signatories: next }))}
              availableSignatories={[
                ...(client.directors || []).map(d => ({ name: d.name, role: d.role || "Director", email: "", ic_no: d.ic_no || "" })),
                ...(client.secretaries || []).map(s => ({ name: s.name, role: "Secretary", email: s.email || "", ic_no: s.ic_no || "" })),
              ].filter(a => a.name && !form.signatories.some(x => (x.name || "").trim().toLowerCase() === a.name.trim().toLowerCase()))}
            />
          ) : (
          <div className="grid grid-cols-2 gap-4">
            {isFormsLetters ? (
              <div className="col-span-2">
                <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Upload PDF</Label>
                <div className="mt-1 flex items-center gap-2">
                  <input ref={uploadRef} type="file" accept="application/pdf" hidden
                         onChange={e => uploadPdf(e.target.files?.[0])}
                         data-testid="upload-form-pdf-input" />
                  <Button variant="outline" className="rounded-sm h-9"
                          onClick={() => uploadRef.current?.click()}
                          disabled={uploading}
                          data-testid="upload-form-pdf-btn">
                    <UploadSimple size={14} weight="bold" className="mr-2" />
                    {uploading ? "Uploading…" : (form.body ? "Replace PDF" : "Choose PDF from computer")}
                  </Button>
                  {form.body && (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#24A148]">
                      PDF loaded — ready to send for e-signature
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Sample Template</Label>
                <Select value={form.template_key} onValueChange={pickTemplate}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="template-select"><SelectValue placeholder="Pick a template…" /></SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.key} value={t.key}>
                        {t.title}
                      </SelectItem>
                    ))}
                    {templates.length === 0 && (
                      <div className="px-2 py-3 text-xs text-muted-foreground">
                        No templates yet. Create some in the Library first.
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!isFormsLetters && (
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Type</Label>
                <Select value={form.resolution_type} onValueChange={v => setForm({...form, resolution_type: v})}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="resolution-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {typeOptions.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2">
              <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Title</Label>
              <Input className="mt-1 rounded-sm" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="resolution-title-input" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Meeting / Circulation Date</Label>
              <Input type="date" className="mt-1 rounded-sm" value={form.meeting_date}
                     onChange={e => setForm({...form, meeting_date: e.target.value})}
                     data-testid="meeting-date-input" />
            </div>
            <div />
            <div className="col-span-2">
              <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Body</Label>
              <div className="mt-1" data-testid="resolution-body-input">
                <RichTextEditor
                  value={toHtml(form.body)}
                  onChange={(html) => setForm({ ...form, body: html })}
                  minHeight={520}
                  placeholders={[
                    { token: "{{COMPANY_NAME}}",     label: "Company" },
                    { token: "{{SSM}}",              label: "SSM" },
                    { token: "{{MEETING_DATE}}",     label: "Meeting Date" },
                    { token: "{{DIRECTORS}}",        label: "Directors" },
                    { token: "{{DIRECTOR_1}}",       label: "Director 1" },
                    { token: "{{DIRECTOR_2}}",       label: "Director 2" },
                    { token: "{{SHAREHOLDER_1}}",    label: "Shareholder 1" },
                    { token: "{{SHAREHOLDER_2}}",    label: "Shareholder 2" },
                    { token: "{{SHARES_1}}",         label: "Shares 1" },
                    { token: "{{SHARES_2}}",         label: "Shares 2" },
                    { token: "{{TOTAL_SHARES}}",     label: "Total Shares" },
                    { token: "{{BUSINESS_ADDRESS}}", label: "Business Address" },
                  ]}
                />
              </div>
            </div>

            <div className="col-span-2 border border-border p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-xs uppercase tracking-widest">Signatories</div>
                <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={addSignatory}
                        data-testid="add-signatory-btn">
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
              {form.signatories.length === 0 && <div className="text-xs text-muted-foreground">No signatories yet.</div>}
              {form.signatories.map((s, i) => (
                <div key={i} className="grid grid-cols-4 gap-2 mb-2">
                  <Input placeholder="Full name" value={s.name} onChange={e => updSignatory(i, "name", e.target.value)} className="rounded-sm" />
                  <Input placeholder="Role" value={s.role} onChange={e => updSignatory(i, "role", e.target.value)} className="rounded-sm" />
                  <Input placeholder="Email (optional)" value={s.email} onChange={e => updSignatory(i, "email", e.target.value)} className="rounded-sm" />
                  <div className="flex gap-2">
                    <Input placeholder="NRIC (optional)" value={s.ic_no} onChange={e => updSignatory(i, "ic_no", e.target.value)} className="rounded-sm" />
                    <Button size="icon" variant="ghost" onClick={() => rmSignatory(i)} className="text-[#DA1E28]"><Trash size={14} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => save(false)}
                    disabled={saving || (isFormsLetters && form.signatories.length === 0)}
                    className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE] disabled:opacity-50 min-w-[280px]"
                    data-testid="save-resolution-btn">
              {saving
                ? "Saving…"
                : (editing?.status === "completed" && isFormsLetters
                    ? "SAVE & REBUILD PDF"
                    : (isFormsLetters ? "Save Form / Letter" : "Save Resolution"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview */}
      <Dialog open={!!viewRes} onOpenChange={o => !o && setViewRes(null)}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{viewRes?.title}</DialogTitle></DialogHeader>
          {viewRes && (
            <>
              <div className="resolution-preview border border-border p-6 bg-white"
                   dangerouslySetInnerHTML={{ __html: sanitize(toHtml(viewRes.body)) }} />
              <div className="grid grid-cols-2 gap-4 mt-4">
                {viewRes.signatories.filter(s => s.signed_at).map(s => (
                  <div key={s.id} className="border border-border p-3">
                    <div className="text-xs font-mono text-muted-foreground">{s.role}</div>
                    <div className="font-medium">{s.name}</div>
                    {s.signature_data_url && <img alt="sig" src={s.signature_data_url} className="mt-2 border border-border bg-white max-h-24" />}
                    <div className="mt-1 text-[10px] font-mono text-muted-foreground">Signed {fmtMY(s.signed_at)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Th = ({ children, className = "" }) =>
  <th className={`px-6 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-normal ${className}`}>{children}</th>;
